import { Component, Inject, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import {
  MatLegacySelectChange as MatSelectChange,
  MatLegacySelectModule as MatSelectModule,
} from '@angular/material/legacy-select';

import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { catchError, EMPTY, filter, finalize, forkJoin, Observable, of, switchMap } from 'rxjs';

import { Store } from '@ngxs/store';
import { TsButtonModule, TSIconModule, TsSnackbarService } from 'tsui';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AuthService } from 'app/core';
import { Permission } from 'app/core/enums';
import { Dictionary } from 'app/shared/models';
import { RelatedToType } from 'app/shared/enums';
import { FieldDef } from 'app/core/models';
import { SharedModule } from 'app/shared/shared.module';
import { PanelDef } from 'app/shared/components/panels/panels.types';
import { FileResource } from 'app/shared/modules/folders/folders.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Project, ProjectTemplate } from 'app/modules/projects/projects.types';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { OffersService } from 'app/modules/projects/details/offers/offers.service';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { isArraysEqual, isEmptyOrWhitespace, keepProps } from 'app/shared/Utils/common.utils';
import { UploadFileTableComponent } from './components/upload-file-table/upload-file-table.component';
import { CustomField, CustomFormField, CustomFormFieldType } from 'app/shared/modules/custom-form/custom-form.types';
import { Offer, OffersDialogDataModel } from 'app/modules/projects/details/offers/offers.types';
import { cloneDeep } from 'lodash-es';
import { DashboardUtils } from '@shared/Utils';
import { Router } from '@angular/router';

@Component({
  selector: 'add-offer',
  templateUrl: './add-offer.component.html',
  styleUrls: ['./add-offer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    MatSelectModule,
    MatTooltipModule,
    MatProgressBarModule,
    SharedModule,
    TsButtonModule,
    TSIconModule,
    UploadFileTableComponent,
  ],
})
export class AddOfferComponent extends OnDestroyMixin implements OnInit {
  // Use this to exclude fields from the 'select a field' dropdown.
  private _fieldsToExclude = ['id'];
  private _selectedFields: CustomField[] = [];
  private _model: Dictionary<any> = {};
  private _projectTemplate: ProjectTemplate<any>;
  private _panelDef: PanelDef<any>;

  offer: Offer;
  project: Project;
  selectedFieldDef: FieldDef;
  currentFields: CustomFormField[] = [];
  ui = { isEditing: false, isLoading: false, isAddingField: false, isSaving: false };
  allowedFields: FieldDef[] = [];

  private get selectedFields(): CustomField[] {
    const fields = this._panelDef?.meta?.dialog_fields
      ? this._panelDef.meta.dialog_fields
      : this._projectTemplate.definition?.offers_dialog_definition
        ? JSON.parse(this._projectTemplate.definition.offers_dialog_definition).fields
        : [
            { name: 'offer_price', isRequired: true, isRemovable: false },
            { name: 'offer_type', isRequired: true, isRemovable: false },
            { name: 'bid_round', isRequired: false, isRemovable: true },
            { name: 'closing_date', isRequired: false, isRemovable: true },
            { name: 'dd_expiration_date', isRequired: false, isRemovable: true },
            { name: 'description', isRequired: false, isRemovable: true },
            { name: 'user_id', isRequired: false, isRemovable: true },
            { name: 'sent_at', isRequired: false, isRemovable: true },
            { name: 'workflow_state', isRequired: false, isRemovable: true },
          ];

    return fields.filter((field: CustomField) => this.allowedFields.find((fieldDef) => fieldDef.name === field.name));
  }

  private get isTemplateDirty(): boolean {
    return !isArraysEqual(this.normalizeArray(this._selectedFields), this.normalizeArray(this.currentFields));
  }

  private get isFormInvalid(): boolean {
    return !this.currentFields
      .filter(({ isRequired }) => isRequired)
      .every((item) => {
        const value = this._model[item.name];

        if (typeof value === 'string') {
          return !isEmptyOrWhitespace(value);
        }

        return !!value;
      });
  }

  get isEditingOffer(): boolean {
    return this.dialogData.offer ? true : false;
  }

  get canEditLayout(): boolean {
    return this.authService.hasPermission(Permission.PROJECT_DASHBOARD_EDIT_LAYOUT);
  }

  get canSaveTemplate(): boolean {
    return this.isTemplateDirty && !this.ui.isAddingField;
  }

  get isSaveButtonDisabled(): boolean {
    return this.isEditingOffer ? false : this.isFormInvalid || this.ui.isSaving;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: OffersDialogDataModel,
    private readonly store: Store,
    private readonly matDialogRef: MatDialogRef<AddOfferComponent>,
    private readonly cdRef: ChangeDetectorRef,
    private readonly offersService: OffersService,
    private readonly foldersTabService: FoldersTabService,
    private readonly confirmService: ConfirmService,
    private readonly snackBar: TsSnackbarService,
    private readonly projectsService: ProjectsService,
    private readonly authService: AuthService,
    private readonly router: Router,
  ) {
    super();

    this.allowedFields = this.store
      .selectSnapshot(
        ObjectsManagerState.getFieldsByObjectName(RelatedToType.Offer, {
          addNestedFields: false,
          allowedFieldsOnly: true,
        }),
      )
      .filter((field) => !this._fieldsToExclude.includes(field.name));
  }

  ngOnInit(): void {
    this.project = this.dialogData.project;

    this._projectTemplate = this.dialogData.projectTemplate;

    this._panelDef = this.dialogData.panelDef;

    this.fetchSelectedFields(this.selectedFields);

    if (this.isEditingOffer) this.offer = this.dialogData.offer;

    this.offersService.projectId = this.project.id;
  }

  private fetchSelectedFields(value: CustomField[]): void {
    this._selectedFields = value;

    this.currentFields = this._selectedFields.map((selectedField: CustomField) =>
      this.toCustomFormField(selectedField),
    );
  }

  private normalizeArray(arr: CustomField[]): CustomField[] {
    return arr.map((item) => keepProps(item, ['name', 'isRequired', 'isRemovable']) as CustomField);
  }

  private toCustomFormField(customField: CustomField): CustomFormField {
    const { name: fieldName } = customField;
    return {
      name: fieldName,
      type: CustomFormFieldType.FieldDef,
      isRemovable: customField?.isRemovable,
      fieldDef: this.allowedFields.find(({ name }) => name === fieldName),
      ...customField,
    };
  }

  private addDocument(offer: Offer, fileToUpload: File) {
    if (fileToUpload) {
      let fileResource: FileResource = {};
      fileResource.folder_resource_id = this.project.root_folder_resource_id;
      fileResource.name = fileToUpload.name;
      fileResource.related_to_type = RelatedToType.Offer;
      fileResource.related_to_id = offer.id;
      this.foldersTabService.moduleId = this.project.id;
      this.foldersTabService.moduleBaseUrl = 'projects';

      return this.foldersTabService.createFile(fileResource.folder_resource_id, fileResource).pipe(
        untilComponentDestroyed(this),
        switchMap((createdFileResource) =>
          this.foldersTabService.uploadDocument(
            createdFileResource.folder_resource_id,
            createdFileResource.id,
            fileToUpload,
          ),
        ),
      );
    }
  }

  getDialogDynamicTitle(): string {
    return this.isEditingOffer ? 'Edit' : 'Add';
  }

  isPlaceholderField(field: CustomFormField): boolean {
    return field.type === CustomFormFieldType.Placeholder;
  }

  toggleEditing(): void {
    if (this.ui.isEditing && this.isTemplateDirty) {
      this.currentFields = this._selectedFields.map((selectedField: CustomField) =>
        this.toCustomFormField(selectedField),
      );
    }

    this.ui = { ...this.ui, isEditing: !this.ui.isEditing, isAddingField: false };
  }

  saveTemplate(): void {
    this._selectedFields = this.normalizeArray(this.currentFields);

    const template: ProjectTemplate<any> = cloneDeep(this._projectTemplate);
    const isOverviewTab = this.router.url.includes('overview');

    let panelIndex: number, laneIndex: number, tabIndex: number, meta: any;

    if (isOverviewTab) {
      panelIndex = DashboardUtils.getLaneAndPanelIndexes(
        this._projectTemplate.definition.dashboard,
        this._panelDef,
      ).panelIndex;
      laneIndex = DashboardUtils.getLaneAndPanelIndexes(
        this._projectTemplate.definition.dashboard,
        this._panelDef,
      ).laneIndex;

      meta = {
        ...template.definition.dashboard.panels[laneIndex][panelIndex].meta,
        dialog_fields: this._selectedFields,
      };

      template.definition.dashboard.panels[laneIndex][panelIndex] = {
        ...template.definition.dashboard.panels[laneIndex][panelIndex],
        meta,
      };
    } else {
      panelIndex = DashboardUtils.getTabAndPanelIndexes(
        this._projectTemplate.definition.dashboard,
        this._panelDef,
      ).panelIndex;
      tabIndex = DashboardUtils.getTabAndPanelIndexes(
        this._projectTemplate.definition.dashboard,
        this._panelDef,
      ).tabIndex;

      meta = {
        ...template.definition.dashboard.tabs[tabIndex].panels[panelIndex].meta,
        dialog_fields: this._selectedFields,
      };

      template.definition.dashboard.tabs[tabIndex].panels[panelIndex] = {
        ...template.definition.dashboard.tabs[tabIndex].panels[panelIndex],
        meta,
      };
    }

    delete template.definition?.offers_dialog_definition;

    this.projectsService
      .updateProjectTemplate(template)
      .pipe(
        untilComponentDestroyed(this),
        switchMap(() => this.projectsService.getProjectTemplateById(this._projectTemplate.id)),
        catchError(() => {
          this.snackBar.open('Failed to save the offers dialog definition. Please try again later.', 'error');
          return EMPTY;
        }),
        finalize(() => {
          this.ui = { ...this.ui, isEditing: !this.ui.isEditing };
          this.cdRef.detectChanges();
        }),
      )
      .subscribe(() => {
        this.snackBar.open('Offers dialog definition saved successfully.', 'success');
      });
  }

  dropped(event: CdkDragDrop<CustomFormField[]>): void {
    moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
  }

  onFieldUpdate(event: Dictionary<any>): void {
    this._model = { ...this._model, ...event };
  }

  onFieldSelected(event: MatSelectChange): void {
    this.selectedFieldDef = event.value as FieldDef;
  }

  isSelected(fieldName: string): boolean {
    return this.currentFields.filter(({ name }) => name === fieldName).length > 0;
  }

  removeField(fieldIndex: number): void {
    if (this.currentFields[fieldIndex].type === CustomFormFieldType.Placeholder) {
      this.ui = { ...this.ui, isAddingField: false };
    }

    this.currentFields.splice(fieldIndex, 1);
  }

  addField(): void {
    this.ui = { ...this.ui, isAddingField: true };
    this.currentFields = [
      ...this.currentFields,
      { name: CustomFormFieldType.Placeholder, type: CustomFormFieldType.Placeholder },
    ];
  }

  saveField(fieldIndex: number): void {
    this.currentFields[fieldIndex] = {
      name: this.selectedFieldDef.name,
      type: CustomFormFieldType.FieldDef,
      fieldDef: this.selectedFieldDef,
    };
    this.ui = { ...this.ui, isAddingField: false };
    this.selectedFieldDef = null;
  }

  discard(): void {
    this.matDialogRef.close();
  }

  submit(): void {
    if (!this.isEditingOffer && !Object.keys(this._model).length) {
      this.snackBar.open('Please fill in at least one field.', 'error');
      return;
    }

    this.ui = { ...this.ui, isLoading: true, isSaving: true };

    const handleOfferOperation = (offerOperation: Observable<Offer>, successMessage: string) => {
      offerOperation
        .pipe(
          untilComponentDestroyed(this),
          switchMap((result) => {
            if (this._model.file_resources) {
              const filesToUpload: File[] = this._model.file_resources.filter((file: unknown) => file instanceof File);

              // Map each file to the addDocument() observable.
              const addDocumentObservables$ = filesToUpload.map((file) => this.addDocument(result, file));

              // Using forkJoin to wait for all file uploads to complete.
              return forkJoin(addDocumentObservables$).pipe(
                // Catch any errors during file uploads.
                catchError(() => {
                  this.snackBar.open('Failed to upload one or more files. Please try again later.', 'error');
                  return EMPTY;
                }),
              );
            }
            return of(result);
          }),
        )
        .subscribe({
          next: () => {
            this.snackBar.open(successMessage, 'success');
          },
          complete: () => {
            this.ui = { ...this.ui, isLoading: false, isSaving: false };
            this.matDialogRef.close(this._model);
          },
        });
    };

    if (this.isEditingOffer) {
      handleOfferOperation(
        this.offersService.updateOffer({ ...this._model, id: this.offer.id } as Offer),
        'Offer updated successfully.',
      );
    } else {
      handleOfferOperation(this.offersService.createOffer(this._model as Offer), 'Offer created successfully.');
    }
  }

  deleteOffer(): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(
        untilComponentDestroyed(this),
        filter((res: boolean) => res),
        switchMap(() => this.offersService.deleteOffer(this.offer.id)),
        catchError(() => {
          this.snackBar.open('Failed to delete the offer. Please try again later.', 'error');
          return EMPTY;
        }),
        finalize(() => this.matDialogRef.close(this._model)),
      )
      .subscribe(() => this.snackBar.open('Offer successfully deleted.', 'success'));
  }

  onClearFile(): void {
    this._model.file = undefined;
  }

  canEditDeal(): boolean {
    return this.project?.permissions?.is_editable;
  }
}
